import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import {
  ConfirmButton,
  ModalButtonWithForm,
  MoreOptions
} from 'components/common';
import { FormGroup, TextArea } from 'components/common/form';

interface Props {
  achievement?: {
    id: number;
    isOwnerOrSuperadmin?: boolean;
    isSupervisor?: boolean;
    userAdminPermissions?: string[];
  };
  allowEmptyText?: boolean;
  canEdit: boolean;
  deleteActionText: string;
  deleteButtonText: string;
  deleteSuccessMessage: string;
  deleteText: string;
  deleteTitle: string;
  editSuccessMessage: string;
  editTextInfoMessage?: string;
  editTextLabel: string;
  editTitle: string;
  id: number;
  targetUserId: number;
  updateUserAchievement?: () => Promise<void>;
  text: string;
}

const AchievementCompletionsFillTemplateActions = ({
  achievement,
  allowEmptyText = true,
  canEdit,
  deleteActionText,
  deleteButtonText,
  deleteSuccessMessage,
  deleteText,
  deleteTitle,
  editSuccessMessage,
  editTextInfoMessage,
  editTextLabel,
  editTitle,
  id,
  targetUserId,
  updateUserAchievement,
  text
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, user } = useReduxState();

  if (
    !achievement ||
    !canEdit ||
    (!achievement.isOwnerOrSuperadmin &&
      !achievement.isSupervisor &&
      !achievement.userAdminPermissions?.includes('manage-users') &&
      !(targetUserId === user?.id))
  ) {
    return null;
  }

  const handleRemoveItem = async () => {
    try {
      await api.delete(
        `/admin/achievements/${achievement.id}/completions/${targetUserId}/items/${id}`
      );
      if (updateUserAchievement) await updateUserAchievement();
      return true;
    } catch (err) {}
  };

  const handleUpdateItem = async ({ comment }: { comment: string }) => {
    try {
      await api.put(
        `/admin/achievements/${achievement.id}/completions/${targetUserId}/items/${id}`,
        { text: comment || null }
      );
      if (updateUserAchievement) await updateUserAchievement();
      dispatch.addNotice({ text: editSuccessMessage, type: 'success' });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <MoreOptions>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="pen"
        buttonText={messages.commonEdit}
        initialValues={{ comment: text }}
        modalActionText={messages.commonSave}
        modalTitle={editTitle}
        modalWidth="medium"
        onSubmit={handleUpdateItem}
        pristineIsValid={false}
      >
        {(formState) => (
          <FormGroup label={editTextLabel} maxWidth="100%">
            <TextArea
              autoGrow={true}
              error={formState.errors?.comment}
              field="comment"
              id={`updateUserComment-${id}`}
              infoMessage={editTextInfoMessage}
              {...(!allowEmptyText
                ? {
                    required: true,
                    validate: (value: string) =>
                      value ? undefined : messages.commonFieldRequired,
                    validateOnChange: true
                  }
                : {})}
            />
          </FormGroup>
        )}
      </ModalButtonWithForm>

      <ConfirmButton
        actionText={deleteActionText}
        buttonIcon="trash-alt"
        buttonSize="small"
        buttonText={deleteButtonText}
        category="danger"
        confirmText={deleteText}
        confirmTitle={deleteTitle}
        onConfirm={handleRemoveItem}
        successMessage={deleteSuccessMessage}
      />
    </MoreOptions>
  );
};

export default AchievementCompletionsFillTemplateActions;
